import { Component } from '@angular/core';
import { TransferHttpService } from 'src/app/services/transfer-http/transfer-http.service';
import { Observable, BehaviorSubject, of, Subject } from 'rxjs';
import { PermissionsService } from 'src/app/services/permissions/permissions.service';
import { DialogComponent, SpinnerText } from 'src/app/services/message/message.enum';
import { MessageService } from 'src/app/services/message/message.service';
import { switchMap, tap, delay, map, filter, take, takeUntil } from 'rxjs/operators';
import { NgEmployeeXpermissionGroup, NgPermissionGroups, NgPermissions, NgUsers } from 'src/models';
import { environment } from 'src/environments/environment';
import { FormBuilder, FormControl, FormGroup, Validators} from '@angular/forms';
import { AimsCommonTablePermissions, TableRowAdd, TableRowDelete, TableRowUpdate, AimsCommonConfirmDialogComponent, AimsCommonTableHeader, AimsCommonButtonComponent, AimsCommonTableHeaderType, AimsCommonPopupEditorComponent } from '@aims/common-components';
import { MatDialog } from '@angular/material/dialog';
import { AuthService } from 'src/app/services/auth/auth.service';
import { UserLookupColumns } from '../permissions.component';
import { GroupPermissionsPopupComponent } from './group-permissions-popup/group-permissions-popup.component';

@Component({
    selector: 'group-permissions',
    templateUrl: './group-permissions.component.html',
    styleUrls:  ['../permissions.component.css', './group-permissions.component.css']
})
export class GroupPermissionsComponent {

    constructor(
        private transferHttp: TransferHttpService,
        private dialog: MatDialog,
        public permissionsService: PermissionsService,
        private messageService: MessageService,
        private authService: AuthService,
        private formBuilder: FormBuilder) {

    }
    //***************************
    // Variables
    //***************************

    canCreate = this.permissionsService.canCreate(this.permissionsService.ComponentID.GroupPermissions);
    canUpdate = this.permissionsService.canUpdate(this.permissionsService.ComponentID.GroupPermissions);
    canDelete = this.permissionsService.canDelete(this.permissionsService.ComponentID.GroupPermissions);
    canCloneGroup = this.permissionsService.canCreate(this.permissionsService.ComponentID.CloneGroup);
    canCloneGroupToDifferentTenant: boolean = this.authService.isAimsAdmin;
    permissions: AimsCommonTablePermissions = { create: this.canCreate, update: this.canUpdate, delete: this.canDelete, export: false }
    clonePermissions: AimsCommonTablePermissions = { create: false, update: false, delete: false, export: false}
    //Dropdown Values
    groupValues = this.permissionsService.getPermissionGroups();
    users = this.permissionsService.getUsers();
    activeUsers = this.getActiveUsers(this.users);
    components = this.permissionsService.getComponents();
    addPermissionsRowInitialObject = {
        component:{
            componentId: '',
            component: '',
            parentComponentId: 1,
            parentComponentName: '',
            description: '',
            inverseParentComponent:'',
            ngPermissions:'',
            parentComponent: {
                component:'',
                componentId:'',
                description:'',
                inverseParentComponent:'',
                ngPermissions:'',
                parentComponent:'',
                parentComponentId:''
            }
        },
        componentId: '',
        create:false,
        delete:false,
        read:false,
        update:false,
        employee:'',
        employeeId:'',
        permissionGroup:'',
        permissionGroupId:'',
        permissionId:'',
        tenant:'',
        tenantId:'',
        user:'',
        userId:''
    }
    addMembershipRowInitialObject = {
        active:false,
        employeeXpermissionGroupId:undefined,
        permissionGroupId:undefined,
        userId:undefined,
        userName:undefined
    }

    //Columns
    groupLookupColumns: AimsCommonTableHeader[] = [
        { key: 'permissionGroupName', label: 'Group Name', type: AimsCommonTableHeaderType.STRING},
        { key: 'description', label: 'Group Description',  type: AimsCommonTableHeaderType.STRING }
    ];
    componentNameLookupColumns: AimsCommonTableHeader[] = [
        { key: 'component', label: 'Component', type: AimsCommonTableHeaderType.STRING },
        { key: 'parentComponentName', label: 'Parent', type: AimsCommonTableHeaderType.STRING },
        { key: 'description', label: 'Description', type: AimsCommonTableHeaderType.HTML }
    ];
    userLookupColumns = UserLookupColumns;

    groupPermissionsColumns: AimsCommonTableHeader[] = [
        { key: 'editButton', label: 'Edit', type: AimsCommonTableHeaderType.EDIT_BUTTON, filter: false },
        { key: 'component.component', label: 'Component', type: AimsCommonTableHeaderType.DYNAMIC, validatorOrOpts: [ Validators.required ] },
        { key: 'component.description', label: 'Component Description', type: AimsCommonTableHeaderType.DYNAMIC },
        { key: 'component.parentComponent.component', label: 'Parent', type: AimsCommonTableHeaderType.DYNAMIC },
        { key: 'create', label: 'Create', type: AimsCommonTableHeaderType.DYNAMIC, filterType:'boolean', width:90},
        { key: 'read', label: 'Read', type: AimsCommonTableHeaderType.DYNAMIC, filterType:'boolean', width:90 },
        { key: 'update', label: 'Update', type: AimsCommonTableHeaderType.DYNAMIC, filterType:'boolean', width:90},
        { key: 'delete', label: 'Delete', type: AimsCommonTableHeaderType.DYNAMIC, filterType:'boolean', width:90 }
    ];
    groupMembershipColumns: AimsCommonTableHeader[] = [
        { key: 'editButton', label: 'Edit', type: AimsCommonTableHeaderType.EDIT_BUTTON, filter: false},
        { key: 'userName', label: 'User Name', type: AimsCommonTableHeaderType.DYNAMIC, validatorOrOpts: [ Validators.required ]},
        { key: 'active', label: 'Active', type: AimsCommonTableHeaderType.DYNAMIC, filterType:'boolean', width:90 }
    ]
    cloneGroupPermissionsColumns: AimsCommonTableHeader[] = [
        { key: 'select', label: 'Clone', type: AimsCommonTableHeaderType.CHECKBOX, filter: false },
        { key: 'component.component', label: 'Component', type: AimsCommonTableHeaderType.DYNAMIC, validatorOrOpts: [ Validators.required ] },
        { key: 'component.description', label: 'Component Description', type: AimsCommonTableHeaderType.DYNAMIC },
        { key: 'component.parentComponent.component', label: 'Parent', type: AimsCommonTableHeaderType.DYNAMIC },
        { key: 'create', label: 'Create', type: AimsCommonTableHeaderType.DYNAMIC, filterType:'boolean', width:90 },
        { key: 'read', label: 'Read', type: AimsCommonTableHeaderType.DYNAMIC, filterType:'boolean', width:90 },
        { key: 'update', label: 'Update', type: AimsCommonTableHeaderType.DYNAMIC, filterType:'boolean', width:90 },
        { key: 'delete', label: 'Delete', type: AimsCommonTableHeaderType.DYNAMIC, filterType:'boolean', width:90 }
    ];

    group: BehaviorSubject<NgPermissionGroups> = new BehaviorSubject(null);
    groupPermissions: Observable<Array<NgPermissions>> = this.group.pipe(
            tap(g => {
                //update the clone group form values
                this.setCloneGroupValues(g?.permissionGroupName,g?.description);
            }),
            delay(0),
            switchMap(g => {
                let r:Observable<Array<NgPermissions>> = of(new Array<NgPermissions>());
                if (g){
                    this.showSpinner = true;
                    this.spinnerText = this.messageService.getSpinnerText(SpinnerText.Load);
                    r = this.permissionsService.getGroupPermissionsOnly(g.permissionGroupId).pipe(
                        tap(p => {
                            this.groupPermissionsDataSource = p
                            this.showSpinner = false;
                        })
                    );
                }
                return r;
            })
    );
    groupMembership: Observable<any>  = this.group.pipe(
        switchMap(g => g ? this.permissionsService.getGroupMembership(g.permissionGroupId):of(null)),
        tap(g => { if (g !== null) {
                this.groupMembershipDataSource = g;
        } })
    );

    groupPermissionsDataSource: NgPermissions[];
    groupMembershipDataSource: NgEmployeeXpermissionGroup[];

    //Spinner
    spinnerText: string = this.messageService.getSpinnerText(SpinnerText.Load);
    showSpinner: boolean = false;

    imgSrc: string = environment.assetsPath;

    //New Group popup
    groupToEdit: NgPermissionGroups;
    groupIsNew: boolean;

    //Clone Group
    isCloneGroup: boolean = false;
    groupToClone: NgPermissionGroups = null;
    cloneGroupSuccess: boolean = false;
    cloneGroupSelectedComponents: any = [];
    // cloneGroupForm = this.formBuilder.group({
    //     name: [null,Validators.required],
    //     description: [null,Validators.required],
    //     tenantId: this.canCloneGroupToDifferentTenant ? [null, Validators.required] : [null]
    // })
    cloneGroupForm = this.formBuilder
      .group({
        name: ['',Validators.required],
        description: ['',Validators.required],
        tenantId: this.canCloneGroupToDifferentTenant ? [0, Validators.required] : [null]
    })


    public emptyElement;
    tenants = this.authService.getTenants();

    get defaultPermission(): NgPermissions {
        let retVal = new NgPermissions();
        if (this.group.value != null) {
            retVal = <NgPermissions>{ permissionGroupId: this.group.value.permissionGroupId };
        }
        return retVal;
    }
    //***************************
    // Functions
    //***************************

    private setCloneGroupValues(name, description){
        this.cloneGroupForm.patchValue({
            name: name ? name : "",
            description: description ? description : ""
        })
    }
    //Event Handlers
    handleGroupChange(dataItem){
        if(!dataItem){
            this.group.next(null);
            return;
        }
        this.group.next(dataItem);
    }
    handlePopupOpen(groupToEdit:NgPermissionGroups, isNew:boolean){
        const dialogRef = this.dialog.open(AimsCommonPopupEditorComponent, {
            data: {
                header: 'Group',
                isNew: isNew,
                content: GroupPermissionsPopupComponent,
                contentData: groupToEdit
            },
            disableClose: true
        })
        dialogRef.afterClosed().subscribe(dialogResult => {
            if(dialogRef && dialogResult.description){
                this.groupToEdit.description = dialogResult.description;
                this.groupToEdit.permissionGroupName = dialogResult.permissionGroupName;
                if(isNew){

                }
                this.handleSaveGroup(this.groupToEdit)
            }
            this.groupToEdit = undefined;

        })
    }
    handleAddGroup(key, type) {
        this.groupIsNew = true;
        this.groupToEdit = new NgPermissionGroups();
        this.handlePopupOpen(this.groupToEdit, this.groupIsNew);
    }
    handleEditGroup(){
        this.groupIsNew = false;
        this.groupToEdit = this.group.value;
        this.handlePopupOpen(this.groupToEdit, this.groupIsNew);
    }
    handleOpenCloneGroup(){
        this.isCloneGroup = true;
        this.groupToClone = this.group.value;
    }

    handleCloneGroupRowClick($event){
        this.cloneGroupSelectedComponents = $event.selections;
    }
    handleCloneGroup(){
        this.spinnerText = this.messageService.getSpinnerText(SpinnerText.Save);
        this.showSpinner = true;

        //Copy the values
        this.groupToClone = {...this.group.value};
        this.groupToClone.description = this.cloneGroupForm.controls['description'].value;
        this.groupToClone.permissionGroupName = this.cloneGroupForm.controls.name.value;
        this.groupToClone.tenantId = this.cloneGroupForm.controls.tenantId.value || this.transferHttp.tenantConfig.tenantId;
        this.groupToClone.tenant = null;
        this.groupToClone.permissionGroupId = 0; //forces it to create a new group

        //Clone the group
        this.spinnerText = this.messageService.getSpinnerText(SpinnerText.Save, "Permission");
        this.showSpinner = true;
        this.permissionsService.savePermissionGroup(this.groupToClone).subscribe(retVal => {
            //Now clone the components
            for(var comp of this.cloneGroupSelectedComponents){
                var permission = new NgPermissions();
                permission = comp;
                permission.permissionId = undefined;
                permission.permissionGroupId = retVal.id;
                permission.tenantId = this.cloneGroupForm.controls.tenantId.value || this.transferHttp.tenantConfig.tenantId;

                this.permissionsService.savePermission(permission).subscribe(retVal => {
                    this.showSpinner = false;
                },
                error => this.showSpinner = false
                );
            }

        },error => this.showSpinner = false,
        () => {
            //completed, get ready for the next clone
            this.showSpinner = false;
            this.cloneGroupForm.reset();
            this.setCloneGroupValues("","");
            this.groupValues = this.permissionsService.getPermissionGroups();
            this.groupPermissionsDataSource = [...this.groupPermissionsDataSource];
            this.isCloneGroup = true;
        }
        );

    }
    handleCancelCloneGroup(){
        this.isCloneGroup = false;
        this.cloneGroupForm.reset();
        this.group.next(null);
    }

    handleDeleteGroup(event) {
        var messsage = this.messageService.getDialogMessage(DialogComponent.DeleteGroupPermissions)
        let dialogRef = this.dialog.open(AimsCommonConfirmDialogComponent, {
            data: messsage
        });
        dialogRef.afterClosed().subscribe(dialogResult => {
            if(dialogResult){
                this.spinnerText = this.messageService.getSpinnerText(SpinnerText.Delete, "Group");
                this.showSpinner = true;

                if (this.group != undefined && this.group != null) {
                    this.permissionsService.deletePermissionGroup(this.group.value.permissionGroupId)
                        .subscribe(retVal => {
                            this.groupValues = this.permissionsService.getPermissionGroups();
                            this.group.next(null);
                        },
                        error => this.showSpinner = false);
                }
                else {
                    alert("You must select a group first");
                }
                this.groupToEdit = undefined;
                this.showSpinner = false;
            }
        });
    }

    handleSaveGroup(group:NgPermissionGroups) {
        this.spinnerText = this.messageService.getSpinnerText(SpinnerText.Save, "Group");
        this.showSpinner = true;
        this.permissionsService.savePermissionGroup(group)
            .subscribe(retVal => {
                this.groupToEdit = undefined;
                this.groupValues = this.permissionsService.getPermissionGroups();
            },
            error => this.showSpinner = false);

        this.showSpinner = false;
    }

    handlePermissionsAimsLookupChange(dataItem, element: NgPermissions, control) {
        //patch values when one control affects another
        if (!dataItem) {
            dataItem = {} // We want to patch below with a null/undefined value
        }
        const parentCtrl = control.parent as FormGroup
        if (!control.parent.controls['component.id']) {
            parentCtrl.addControl('component.componentId', new FormControl(dataItem.componentId))
            parentCtrl.addControl('component.parentComponentId', new FormControl(dataItem.parentComponentId))
            parentCtrl.addControl('component.parentComponentName', new FormControl(dataItem.parentComponentName))
            parentCtrl.addControl('component.parentComponent', new FormControl({parentComponentId: dataItem.parentComponentId, parentComponentName: dataItem.parentComponentName}))
            parentCtrl.addControl('componentId', new FormControl(dataItem.componentId))
            parentCtrl.addControl('ngPermissions', new FormControl(dataItem.ngPermissions))
        }
        control.parent.patchValue({
            'component': dataItem,
            'componentId': dataItem,
            'component.parentComponentId': dataItem.parentComponentId,
            'component.parentComponentName': dataItem.parentComponentName,
            'component.parentComponent': {parentComponentId: dataItem.parentComponentId, parentComponentName: dataItem.parentComponentName},
            'component.id': dataItem.componentId,
            'component.componentId': dataItem.componentId,
            'component.component': dataItem.component,
            'component.description': dataItem.description
        });
        parentCtrl.markAsTouched()
    }

    handleMembershipAimsLookupChange(dataItem, element:any, control) {
        if(!dataItem) {
            dataItem = {}
        }
        control.parent.patchValue({
            'userId':dataItem.userId,
            'userName':dataItem.userName,
            'active':dataItem.active
        });
        element.userId = dataItem.userId;
        element.userName = dataItem.userName;
        element.active = dataItem.active;

    }

    handlePermissionsRowAdd(update: TableRowUpdate){
        this.groupPermissionsDataSource = [update.newRow as NgPermissions, ...this.groupPermissionsDataSource];
        const row = update.newRow as NgPermissions;
        row.create = row.create === true;
        row.delete = row.delete === true;
        row.update = row.update === true;
        row.read = row.read === true;
        this.handlePermissionsRowUpdate(update, true)
    }

    handleMembershipRowAdd(update: TableRowUpdate){
        this.groupMembershipDataSource = [update.newRow as NgEmployeeXpermissionGroup, ...this.groupMembershipDataSource];
        const row = update.newRow as NgEmployeeXpermissionGroup;

        this.handleMembershipRowUpdate(update, true)
    }

    handlePermissionsRowCancelled(update: TableRowUpdate): void {
        // Remove temp if cancelled
        if (!(update.oldRow as NgPermissions).permissionId) {
            this.groupPermissionsDataSource = this.groupPermissionsDataSource.filter(i => i.permissionId)
        }
    }
    handleMembershipRowCancelled(update:TableRowUpdate):void{
        // Remove temp if cancelled
        if (!(update.oldRow as NgEmployeeXpermissionGroup).userId) {
            this.groupMembershipDataSource = this.groupMembershipDataSource.filter(i => i.userId)
        }
    }

    handlePermissionsRowUpdate(update: TableRowUpdate, add?: boolean): void {
    	var row = update.newRow as NgPermissions;
        this.groupPermissionsDataSource[update.index] = update.newRow as NgPermissions
        this.groupPermissionsDataSource = [...this.groupPermissionsDataSource]
        //save NgPermissions

        //Create save object, can't reuse row b/c spread operator flattened it
        var permission = new NgPermissions();
        if(add) { permission.permissionId = undefined } else { permission.permissionId = row.permissionId; }
        permission.component = row.component;
        permission.permissionGroup = this.group.value;
        permission.permissionGroupId = this.group.value.permissionGroupId || row.permissionGroupId;
        permission.tenantId = this.transferHttp.tenantConfig.tenantId || row.tenantId;
        permission.componentId = row.component.componentId;
        permission.create = row.create;
        permission.delete = row.delete;
        permission.read = row.read;
        permission.update = row.update;
        this.spinnerText = this.messageService.getSpinnerText(SpinnerText.Save, "Permission");
        this.showSpinner = true;
        this.permissionsService.savePermission(permission)
            .subscribe(retVal => {
                this.showSpinner = false;
                if (add) { // Need to update the added object's id
                    this.groupPermissionsDataSource[0].permissionId = retVal.savedEntity.permissionId;
                    this.groupPermissionsDataSource = [...this.groupPermissionsDataSource];
                }
            },
            error => this.showSpinner = false);
    }

    handleMembershipRowUpdate(update: TableRowUpdate, add?:boolean): void {
        const row = update.newRow as NgEmployeeXpermissionGroup;
        this.groupMembershipDataSource[update.index] = update.newRow as NgEmployeeXpermissionGroup;
        this.groupMembershipDataSource = [...this.groupMembershipDataSource];

        row.permissionGroupId = this.group.value.permissionGroupId || row.permissionGroupId;

        //save
        if(add){row.employeeXpermissionGroupId = undefined}
        this.spinnerText = this.messageService.getSpinnerText(SpinnerText.Save, "Membership");
        this.showSpinner = true;
        this.permissionsService.saveMembership(row.employeeXpermissionGroupId, row.permissionGroupId, row.userId, row.employeeId)
            .subscribe(retVal => {
                this.showSpinner = false;
                if(add){
                    this.groupMembershipDataSource[0].userId = retVal.savedEntity.userId;
                    this.groupMembershipDataSource = [...this.groupMembershipDataSource];
                }
            },
            error => this.showSpinner = false
            );
    }

    handlePermissionsRowDelete(row: TableRowDelete):void{
        //update the datasource
        this.groupPermissionsDataSource.splice(row.index, 1);
        this.groupPermissionsDataSource = [...this.groupPermissionsDataSource];

        //save
        const permission = row.row as NgPermissions;
        if(permission && permission.permissionId){
            this.spinnerText = this.messageService.getSpinnerText(SpinnerText.Delete, "Permission");
            this.showSpinner = true;
            this.permissionsService.deletePermission(permission.permissionId)
                .subscribe(retVal => {
                    this.showSpinner = false;
                },
                error => this.showSpinner = false);
        }
    }
    handleMembershipRowDelete(row:TableRowDelete):void{
        //update dataSource
        this.groupMembershipDataSource.splice(row.index, 1);
        this.groupMembershipDataSource = [...this.groupMembershipDataSource];

        //save
        const membership = row.row as NgEmployeeXpermissionGroup;
        if(membership && membership.employeeXpermissionGroupId){
            this.spinnerText = this.messageService.getSpinnerText(SpinnerText.Delete, "Group Membership")
            this.showSpinner = true;
            this.permissionsService.deleteGroupMember(membership.employeeXpermissionGroupId)
                .subscribe(retVal => {
                    this.showSpinner = false;
                },
                error => this.showSpinner = false);
        }
    }

    public getActiveUsers(users:Observable<any>) {
        var data = users.pipe(map(results => results.filter(user => user.active)));
        return data
    }


    //***************************
    // Init
    //***************************

    ngOnInit(): void {

    }
}
